// src/components/LevelSidebarNav.jsx
import React, { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { getMaxUnlockedLevel } from "../services/unlockRules";
import { TrophyStorage } from "../services/trophyStorage";

function PillButton({ children, className = "", ...rest }) {
  return (
    <button
      type="button"
      className={
        "px-4 py-1.5 rounded-full bg-[#FFC94E] border-[3px] border-[#456EFF] shadow-[0_6px_0_#E0862B] " +
        "text-[10px] sm:text-xs md:text-sm font-extrabold uppercase text-slate-900 active:translate-y-[2px] " +
        "active:shadow-[0_4px_0_#E0862B] transition-transform " +
        className
      }
      {...rest}
    >
      {children}
    </button>
  );
}

function getLevelPath(level) {
  // Your “real” Level 2 page currently lives here:
  return `/level-${Number(level)}`;
}

export default function LevelSidebarNav({
  currentLevel = 1,
  totalLevels = 10,
  className = "",
}) {
  const navigate = useNavigate();

  const { maxUnlocked, levelsCompleted } = useMemo(() => {
    const maxUnlocked = Math.max(1, Number(getMaxUnlockedLevel?.() || 1));

    const s = TrophyStorage.getState?.();
    const doneMap = s?.roundInProgress?.levelsCompleted || {};
    // normalize keys to numbers
    const done = new Set(
      Object.keys(doneMap)
        .filter((k) => doneMap[k])
        .map((k) => Number(k))
        .filter((n) => Number.isFinite(n))
    );

    return { maxUnlocked, levelsCompleted: done };
  }, []);

  return (
    <aside className={"hidden sm:flex flex-col items-center pt-3 pb-3 w-24 " + className}>
      <PillButton className="mb-4 text-[9px] px-3 py-1" onClick={() => navigate("/home")}>
        Home
      </PillButton>

      {Array.from({ length: totalLevels }).map((_, i) => {
        const level = i + 1;
        const isActive = Number(level) === Number(currentLevel);
        const isUnlocked = level <= maxUnlocked;
        const isDone = levelsCompleted.has(level);

        const base =
          "w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-semibold mb-2 transition";

        const style = isActive
          ? "bg-gradient-to-b from-[#FFBFF0] to-[#E9C5FF] text-slate-900 shadow"
          : isDone
          ? "bg-emerald-100 text-emerald-700"
          : "bg-white text-slate-400 border border-slate-300";

        const lockedStyle = !isUnlocked ? "opacity-40 cursor-not-allowed" : "cursor-pointer hover:scale-[1.06]";

        return (
          <button
            key={level}
            type="button"
            disabled={!isUnlocked}
            onClick={() => {
              if (!isUnlocked) return;
              navigate(getLevelPath(level));
            }}
            className={base + " " + style + " " + lockedStyle}
            title={!isUnlocked ? `Locked • finish Level ${maxUnlocked} first` : `Go to Level ${level}`}
          >
            {level}
          </button>
        );
      })}

      <span className="mt-1 text-[9px] uppercase tracking-[0.18em] text-slate-500">
        Levels
      </span>

      <img
        src="/images/pan-the-panda.png"
        className="mt-4 h-20 object-contain drop-shadow"
        alt=""
      />
    </aside>
  );
}